import {
  AdditiveBlending,
  BoxGeometry,
  Color,
  CylinderGeometry,
  InstancedMesh,
  Mesh,
  MeshBasicMaterial,
  MeshStandardMaterial,
  Object3D,
  PointLight,
  SphereGeometry,
  TorusGeometry,
  Vector3,
} from 'three';
import { mergeParts } from '../geometry/merge.js';
import { Layer } from './Layer.js';
import { mulberry32 } from './Seashells.js';

/**
 * "In the square there is a wall where the old men sit and watch the youth go
 * by; he is seated in a row with them."
 *
 * The square is the floor of the shaft. A low wall curves along one side; the
 * old men are seated on it, facing the stair you are coming down. They are
 * nearly black, because the eye at that hour cannot do better.
 *
 * The youth are the pale shapes crossing the square, always moving, never
 * near enough to be anybody.
 *
 * At the very end one seat is left, and it is yours.
 */
export class Elders extends Layer {
  static id = 'elders';
  static order = 60;

  async build() {
    const { settings, palette, helix } = this.ctx;
    const cfg = settings.elders;
    this.cfg = cfg;

    const floorY = helix.topY - settings.stair.height - 1.2;
    this.floorY = floorY;
    const wallRadius = settings.stair.innerRadius - 0.6;

    this.wallMat = new MeshStandardMaterial({
      color: palette.get('stoneDeep').clone(),
      roughness: 0.95,
      metalness: 0,
      transparent: true,
      opacity: 0,
    });
    this.elderMat = new MeshStandardMaterial({
      color: palette.get('elder').clone(),
      roughness: 0.82,
      metalness: 0,
      transparent: true,
      opacity: 0,
    });
    this.youthMat = new MeshBasicMaterial({
      color: palette.get('youth').clone(),
      transparent: true,
      opacity: 0,
      blending: AdditiveBlending,
      depthWrite: false,
    });

    // ── the wall ─────────────────────────────────────────────────────────
    const arc = new TorusGeometry(wallRadius, 0.001, 2, 96, Math.PI * 1.25);
    arc.dispose();
    const wall = new CylinderGeometry(wallRadius + 0.3, wallRadius + 0.3, 0.52, 80, 1, true, 0, Math.PI * 1.3);
    wall.translate(0, floorY + 0.26, 0);
    const cap = new CylinderGeometry(wallRadius + 0.42, wallRadius + 0.42, 0.1, 80, 1, true, 0, Math.PI * 1.3);
    cap.translate(0, floorY + 0.55, 0);
    const wallMesh = new Mesh(mergeParts([wall, cap]), this.wallMat);
    wallMesh.material.side = 2; // DoubleSide: it is a thin sitting wall
    this.group.add(wallMesh);

    // ── the old men ──────────────────────────────────────────────────────
    const figure = seatedFigure();
    const rand = mulberry32(0x01de25);
    const count = cfg.count;
    const elders = new InstancedMesh(figure, this.elderMat, count);
    elders.frustumCulled = false;
    const dummy = new Object3D();
    this.seats = [];
    for (let i = 0; i < count; i++) {
      // Seated along the arc, with one gap: the place that is kept.
      const u = (i + 0.5) / count;
      const a = u * Math.PI * 1.22 + 0.08;
      const r = wallRadius + 0.05;
      const x = Math.cos(a) * r;
      const z = Math.sin(a) * r;
      dummy.position.set(x, floorY + 0.52, z);
      // Facing out across the square, toward the foot of the stair.
      dummy.rotation.set(0, -a + Math.PI / 2 + (rand() - 0.5) * 0.3, 0);
      const h = 0.92 + rand() * 0.16;
      dummy.scale.set(h, h, h);
      dummy.updateMatrix();
      elders.setMatrixAt(i, dummy.matrix);
      this.seats.push({ x, z, a });
    }
    elders.instanceMatrix.needsUpdate = true;
    this.elders = elders;
    this.group.add(elders);

    // Where the descent finally rests its eyes: the middle of the row.
    const midA = Math.PI * 0.61 + 0.08;
    this.ctx.arrival = new Vector3(
      Math.cos(midA) * (wallRadius + 0.05),
      floorY + 0.75,
      Math.sin(midA) * (wallRadius + 0.05),
    );

    // The empty place, marked only by a worn hollow in the stone.
    const lastA = Math.PI * 1.22 + 0.16;
    this.emptySeat = {
      x: Math.cos(lastA) * (wallRadius + 0.05),
      z: Math.sin(lastA) * (wallRadius + 0.05),
    };
    const hollow = new Mesh(
      new SphereGeometry(0.3, 16, 8, 0, Math.PI * 2, 0, Math.PI / 2),
      this.wallMat,
    );
    hollow.scale.set(1, 0.14, 0.8);
    hollow.position.set(this.emptySeat.x, floorY + 0.55, this.emptySeat.z);
    this.group.add(hollow);

    // ── the youth ────────────────────────────────────────────────────────
    const walker = standingFigure();
    const youthCount = 11;
    const youth = new InstancedMesh(walker, this.youthMat, youthCount);
    youth.frustumCulled = false;
    this.youth = youth;
    this.youthState = [];
    for (let i = 0; i < youthCount; i++) {
      this.youthState.push({
        radius: 2.4 + rand() * 4.4,
        speed: (rand() < 0.5 ? -1 : 1) * (0.1 + rand() * 0.22),
        phase: rand() * Math.PI * 2,
        bob: rand() * Math.PI * 2,
        height: 0.9 + rand() * 0.22,
      });
    }
    this.group.add(youth);

    // ── lamplight ────────────────────────────────────────────────────────
    // Four standing lamps around the square. Without them the last page of
    // the descent is a black room, and the whole point is that you can see
    // the old men clearly and still arrive too late.
    this.lampMat = new MeshBasicMaterial({
      color: palette.get('lamp').clone(),
      transparent: true,
      opacity: 0,
      toneMapped: false,
    });
    this.haloMat = new MeshBasicMaterial({
      color: palette.get('lamp').clone(),
      transparent: true,
      opacity: 0,
      blending: AdditiveBlending,
      depthWrite: false,
    });
    this.lights = [];
    const lampGeo = new SphereGeometry(0.13, 14, 10);
    // Small: the bloom does most of the glow, and a large additive sphere
    // reads as a lens flare pasted over the square.
    const haloGeo = new SphereGeometry(0.26, 14, 10);
    const postGeo = new CylinderGeometry(0.045, 0.07, 3.1, 10);
    for (let i = 0; i < 4; i++) {
      // Out in the middle of the square, so the old men are seen against
      // the light rather than by it.
      const a = (i / 4) * Math.PI * 2 + 0.6;
      const r = 4.2;
      const x = Math.cos(a) * r;
      const z = Math.sin(a) * r;

      const post = new Mesh(postGeo, this.wallMat);
      post.position.set(x, floorY + 1.55, z);
      this.group.add(post);

      const bulb = new Mesh(lampGeo, this.lampMat);
      bulb.position.set(x, floorY + 3.2, z);
      this.group.add(bulb);

      const halo = new Mesh(haloGeo, this.haloMat);
      halo.position.copy(bulb.position);
      this.group.add(halo);

      const light = new PointLight(0xffb272, 0, 34, 2);
      light.position.copy(bulb.position);
      this.group.add(light);
      this.lights.push(light);
    }

    // Two more lamps set along the wall itself. The square's lamps put the
    // old men in silhouette, which is true to the hour but leaves them as
    // holes in the picture; these rake across the row so you can see that
    // they are men, and that one place is free.
    for (const frac of [0.3, 0.8]) {
      const a = Math.PI * 1.22 * frac + 0.1;
      const r = wallRadius + 1.5;
      const x = Math.cos(a) * r;
      const z = Math.sin(a) * r;

      const bulb = new Mesh(lampGeo, this.lampMat);
      bulb.position.set(x, floorY + 2.25, z);
      this.group.add(bulb);

      const halo = new Mesh(haloGeo, this.haloMat);
      halo.position.copy(bulb.position);
      this.group.add(halo);

      const light = new PointLight(0xffc48a, 0, 20, 2);
      light.position.copy(bulb.position);
      this.group.add(light);
      this.lights.push(light);
    }

    this.presence = 0;
    this._dummy = new Object3D();
  }

  update(dt, state) {
    const { palette } = this.ctx;
    const want = smoothstep(this.cfg.appearsAt - 0.06, Math.min(1, this.cfg.appearsAt + 0.09), state.t);
    this.presence += (want - this.presence) * Math.min(1, dt * 2.2);
    const p = this.presence;

    this.group.visible = p > 0.003;
    if (!this.group.visible) return;

    this.wallMat.opacity = p;
    this.elderMat.opacity = p;
    this.elderMat.color.copy(palette.get('elder'));
    this.wallMat.color.copy(palette.get('stoneDeep')).multiplyScalar(0.75);
    this.youthMat.color.copy(palette.get('youth'));
    this.youthMat.opacity = p * 0.3;

    const lamp = palette.get('lamp');
    this.lampMat.color.copy(lamp);
    this.lampMat.opacity = p;
    this.haloMat.color.copy(lamp);
    this.haloMat.opacity = p * 0.09;
    for (let i = 0; i < this.lights.length; i++) {
      const flicker = 0.92 + 0.08 * Math.sin(state.elapsed * (1.7 + i * 0.6) + i * 2.3);
      this.lights[i].color.copy(lamp);
      // The last two are the wall lamps: closer in, and softer.
      const strength = i < 4 ? 30 : 13;
      this.lights[i].intensity = p * strength * flicker;
    }

    const d = this._dummy;
    for (let i = 0; i < this.youthState.length; i++) {
      const y = this.youthState[i];
      const a = y.phase + state.elapsed * y.speed;
      d.position.set(
        Math.cos(a) * y.radius,
        this.floorY + Math.abs(Math.sin(state.elapsed * 2.6 + y.bob)) * 0.03,
        Math.sin(a) * y.radius,
      );
      d.rotation.set(0, -a + Math.PI / 2 + (y.speed > 0 ? 0 : Math.PI), 0);
      d.scale.setScalar(y.height);
      d.updateMatrix();
      this.youth.setMatrixAt(i, d.matrix);
    }
    this.youth.instanceMatrix.needsUpdate = true;
  }

  stats() {
    return { elders: this.cfg.count, presence: this.presence.toFixed(2) };
  }
}

/** An old man, seated: knees, a bent back, a bowed head. Nothing more. */
function seatedFigure() {
  const parts = [];
  const torso = new CylinderGeometry(0.17, 0.23, 0.56, 10);
  torso.rotateX(0.2);
  torso.translate(0, 0.3, -0.03);
  parts.push(torso);

  const head = new SphereGeometry(0.1, 12, 10);
  head.scale(1, 1.12, 1);
  head.translate(0, 0.66, 0.05);
  parts.push(head);

  // Thighs forward, shins down: the shape of sitting.
  for (const side of [-0.1, 0.1]) {
    const thigh = new BoxGeometry(0.14, 0.13, 0.4);
    thigh.translate(side, 0.03, 0.2);
    parts.push(thigh);
    const shin = new BoxGeometry(0.12, 0.44, 0.13);
    shin.translate(side, -0.2, 0.36);
    parts.push(shin);
    const arm = new CylinderGeometry(0.045, 0.05, 0.42, 8);
    arm.rotateX(0.75);
    arm.translate(side * 1.9, 0.28, 0.12);
    parts.push(arm);
  }
  const geo = mergeParts(parts);
  geo.computeVertexNormals();
  return geo;
}

/** Someone going past, seen from a long way up a stair. */
function standingFigure() {
  const parts = [];
  const body = new CylinderGeometry(0.1, 0.16, 1.02, 8);
  body.translate(0, 0.62, 0);
  parts.push(body);
  const head = new SphereGeometry(0.085, 10, 8);
  head.translate(0, 1.22, 0);
  parts.push(head);
  for (const side of [-0.07, 0.07]) {
    const leg = new CylinderGeometry(0.05, 0.04, 0.6, 6);
    leg.translate(side, 0.3, 0);
    parts.push(leg);
  }
  const geo = mergeParts(parts);
  geo.computeVertexNormals();
  return geo;
}

function smoothstep(a, b, x) {
  const t = Math.min(1, Math.max(0, (x - a) / (b - a || 1e-6)));
  return t * t * (3 - 2 * t);
}
